import AssignmentIcon from '@mui/icons-material/Assignment'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  Snackbar,
  Typography,
} from '@mui/material'
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { canUseLegacyAgreement } from '../../helpers/driverAgreement'
import { useGetDocuments } from '../../hooks/api/documents/useGetDocuments'
import { useDriverAgreements } from '../../hooks/api/driverAgreements/useDriverAgreements'
import { useGetDriverProfile } from '../../hooks/api/drivers/useGetDriverProfile'
import { useGetProvinceById } from '../../hooks/api/provinces/useGetProvinceById'
import { AgreementRecord } from '../../types/driverAgreements'
import { DocumentItem } from '../../types/interfaces'
import DriverAgreementModal from '../components/documents/DriverAgreementModal'
import MasterDocuments from '../components/documents/MasterDocuments'
import TextDocs from '../components/documents/TextDocs'
import VehiclePhotos from '../components/documents/VehiclePhotos'
import VersionedDriverAgreement from '../components/documents/VersionedDriverAgreement'
import LoadingDocuments from '../components/loading/Documents'
import { useDriverCity } from '../hooks/useDriverCity'

const Documents = () => {
  const { data, error, isLoading, refetch } = useGetDocuments()
  const { data: driverProfile } = useGetDriverProfile()
  const versionedAgreement = useDriverAgreements(driverProfile?.city?.id)
  const city = useDriverCity()
  const [userDocuments, setUserDocuments] = useState<DocumentItem | null>(null)
  const [vehicleDocuments, setVehicleDocuments] = useState<DocumentItem | null>(
    null
  )
  const [extraDocuments, setExtraDocuments] = useState<DocumentItem | null>(
    null
  )
  const [additionalDocuments, setAdditionalDocuments] =
    useState<DocumentItem | null>(null)
  const { t } = useTranslation()
  type Opening = {
    kind: 'legacy' | 'versioned'
    language?: string
    legacyData?: ComponentProps<typeof DriverAgreementModal>['agreementData']
    record?: AgreementRecord
    uid: string
  }
  const [opening, setOpening] = useState<null | Opening>(null)
  const activeUid = useRef(versionedAgreement.uid)
  activeUid.current = versionedAgreement.uid
  const currentOpening = useRef(opening)
  currentOpening.current = opening
  const [signedNotice, setSignedNotice] = useState(false)
  useEffect(() => {
    setOpening(null)
    setSignedNotice(false)
  }, [versionedAgreement.uid])
  const [driverAgreementData, setDriverAgreementData] = useState(null)
  const [agreementLanguage, setAgreementLanguage] = useState('en')

  // Get province data to determine country
  const { data: provinceData } = useGetProvinceById(
    driverProfile?.city?.province_id || 0,
    (province) => province.country
  )

  // Check if user is from Canada
  const isCanadianApplicant = useMemo(
    () => provinceData === 'CA',
    [provinceData]
  )

  const findDriverAgreement = useCallback(() => {
    const englishAgreement = additionalDocuments?.find(
      (doc) =>
        doc.name === 'Driver Agreement' ||
        doc.document.custom_name === 'Driver Agreement'
    )
    const spanishAgreement = additionalDocuments?.find(
      (doc) =>
        doc.name === 'Acuerdo del Conductor' ||
        doc.document.custom_name === 'Acuerdo del Conductor'
    )
    let agreement = {}
    if (englishAgreement) {
      setAgreementLanguage('en')
      agreement = englishAgreement
    } else if (spanishAgreement) {
      setAgreementLanguage('es')
      agreement = spanishAgreement
    }
    return agreement
  }, [additionalDocuments])

  useEffect(() => {
    if (data && !isLoading) {
      setUserDocuments(data.user_documents)
      setVehicleDocuments(data.vehicle_documents)
      setAdditionalDocuments(data.additional_documents)
      setExtraDocuments(data.extra_documents)
    }
  }, [data, isLoading])

  useEffect(() => {
    const agreement = findDriverAgreement()
    if (agreement) {
      setDriverAgreementData(agreement)
    }
  }, [findDriverAgreement, data])

  const filteredDocuments = useMemo(
    () =>
      additionalDocuments?.filter(
        (doc) =>
          doc.name !== 'Driver Agreement' &&
          doc.name !== 'Acuerdo del Conductor' &&
          doc.document.custom_name !== 'Driver Agreement' &&
          doc.document.custom_name !== 'Acuerdo del Conductor'
      ) || [],
    [additionalDocuments]
  )

  const history = versionedAgreement.acceptedRecord
  const enabled =
    versionedAgreement.data?.mode === 'enabled' && !versionedAgreement.isError
  const legacy =
    canUseLegacyAgreement(
      versionedAgreement.data,
      versionedAgreement.isError,
      versionedAgreement.isFetching
    ) && !history
  const viewer = enabled || Boolean(history)
  const entry = (
    <Box alignItems="center" display="flex" justifyContent="space-between">
      {viewer && (
        <Button
          onClick={() =>
            setOpening({
              kind: 'versioned',
              record: enabled ? undefined : (history ?? undefined),
              uid: versionedAgreement.uid,
            })
          }
          startIcon={<AssignmentIcon />}
          variant="contained"
        >
          {t('agreement.title')}
        </Button>
      )}
      {versionedAgreement.isError && (
        <Alert
          action={
            <Button
              onClick={() => {
                versionedAgreement.refetch()
              }}
            >
              {t('agreement.retry')}
            </Button>
          }
          severity="warning"
        >
          {t('agreement.unavailable')}
        </Alert>
      )}
      {legacy &&
        additionalDocuments &&
        driverAgreementData?.status !== 'approved' &&
        isCanadianApplicant && (
          <Button
            color="primary"
            onClick={() =>
              setOpening({
                kind: 'legacy',
                language: agreementLanguage,
                legacyData: driverAgreementData,
                uid: versionedAgreement.uid,
              })
            }
            startIcon={<AssignmentIcon />}
            variant="contained"
          >
            {t('auth.register.driver_agreement_to_sign')}
          </Button>
        )}
    </Box>
  )
  const isCurrentOpening = () =>
    opening &&
    activeUid.current === opening.uid &&
    currentOpening.current === opening
  const closeAgreement = () => {
    if (isCurrentOpening()) setOpening(null)
  }
  const agreementDialog =
    opening &&
    opening.uid === versionedAgreement.uid &&
    (opening.kind === 'versioned' ? (
      <Dialog fullWidth maxWidth="sm" onClose={closeAgreement} open>
        <DialogTitle>{t('agreement.title')}</DialogTitle>
        <DialogContent>
          <VersionedDriverAgreement
            key={opening.uid}
            knownRecord={opening.record}
            onStatusChange={(record) => {
              if (!isCurrentOpening()) return
              versionedAgreement.rememberAccepted(record)
              versionedAgreement.refetch().catch(() => undefined)
            }}
            uid={opening.uid}
          />
          <Button onClick={closeAgreement}>{t('agreement.back')}</Button>
        </DialogContent>
      </Dialog>
    ) : (
      <DriverAgreementModal
        agreementData={opening.legacyData}
        isPending={opening.legacyData?.status !== 'approved'}
        language={opening.language}
        onClose={closeAgreement}
        onSuccess={async () => {
          if (!isCurrentOpening()) return
          setSignedNotice(true)
          setOpening(null)
          // Consent succeeded. A background list read cannot reverse that result.
          refetch().catch(() => undefined)
        }}
        open
      />
    ))
  const notification = (
    <Snackbar
      anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
      autoHideDuration={6000}
      onClose={() => setSignedNotice(false)}
      open={signedNotice}
    >
      <Alert onClose={() => setSignedNotice(false)} severity="success">
        {t('auth.register.driver_agreement_sucess')}
      </Alert>
    </Snackbar>
  )
  if (isLoading)
    return (
      <>
        {entry}
        <LoadingDocuments />
        {agreementDialog}
        {notification}
      </>
    )
  if (error)
    return (
      <>
        {entry}
        <Alert severity="error">{t('errors.something_wrong')}</Alert>
        {agreementDialog}
        {notification}
      </>
    )

  return (
    <>
      <Grid container justifyContent="center" sx={{ p: 2, pb: 8 }}>
        <Grid item md={4} xs={12}>
          {entry}
          <Typography sx={{ fontWeight: 'bold', pt: 2 }} variant="h6">
            {t('documents.info.driver_docs')}
          </Typography>
          <List>
            {userDocuments &&
              userDocuments.map((item) => (
                <MasterDocuments
                  city={city}
                  data={item}
                  key={item.document.id}
                />
              ))}
          </List>
          <Typography sx={{ fontWeight: 'bold', pt: 2 }} variant="h6">
            {t('documents.info.vehicle_docs')}
          </Typography>
          {vehicleDocuments &&
            vehicleDocuments.map((vehicle) => (
              <List key={vehicle.vehicle_id}>
                <Typography variant="h6">
                  {t('profile.menu.vehicle')} {vehicle.plate_number}
                </Typography>
                <VehiclePhotos
                  city={city}
                  vehicleId={vehicle.vehicle_id as number}
                />
                {vehicle.documents &&
                  vehicle.documents.map((item) => (
                    <MasterDocuments
                      city={city}
                      data={item}
                      key={item.document.id}
                      vehicleId={vehicle.vehicle_id as number}
                    />
                  ))}
              </List>
            ))}
          <Typography sx={{ fontWeight: 'bold', pt: 2 }} variant="h6">
            {t('documents.info.additional_docs')}
          </Typography>
          <List>
            {filteredDocuments &&
              filteredDocuments.map((item) => (
                <MasterDocuments
                  city={city}
                  customName={item.document.custom_name}
                  data={item}
                  key={item.document.id}
                />
              ))}
            {extraDocuments &&
              extraDocuments.map((item) => <TextDocs data={item} />)}
          </List>
        </Grid>
      </Grid>
      {agreementDialog}
      {notification}
    </>
  )
}

export default Documents
